import * as React from "react";

import { Link, type LinkProps } from "@/components/ui/link";
import { cn } from "@/lib/utils";

export interface ULThemeLinkProps extends LinkProps {
  /**
   * The content of the link element.
   */
  children: React.ReactNode;
  /**
   * Additional class names for custom styling.
   */
  className?: string;
  /**
   * Optional flag to disable the link.
   */
  disabled?: boolean;
  /**
   * Bold links (e.g. standalone "back" navigation) use the bold link weight
   * token instead of the normal in-text link weight.
   */
  bold?: boolean;
  /**
   * Optional ref for the link element.
   */
  ref?: React.Ref<HTMLAnchorElement>;
}

const ULThemeLink = ({
  children,
  className,
  disabled = false,
  bold = false,
  ref,
  ...props
}: ULThemeLinkProps) => {
  // Base component styles
  const baseStyles = cn(
    "text-link-focus text-(length:--ul-theme-font-links-size) focus:rounded-(--ul-theme-border-links-border-radius) hover:text-link-focus/80",
    bold
      ? "font-(weight:--ul-theme-font-links-weight-bold)"
      : "font-(weight:--ul-theme-font-links-weight)"
  );

  // Disabled state styles
  const disabledStyles = disabled ? "pointer-events-none text-muted cursor-not-allowed" : "";

  // UL theme overrides
  const variantThemeOverrides =
    "theme-universal:focus:outline-none theme-universal:focus:ring-4 theme-universal:focus:ring-base-focus/15 theme-universal:focus:bg-base-focus/15"; // focus base color

  // Always underline links per WCAG 1.4.1 and Thon design spec (#34)
  const linkStyleValue = "always";

  return (
    <Link
      ref={ref}
      className={cn(baseStyles, disabledStyles, variantThemeOverrides, className)}
      underline={linkStyleValue}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </Link>
  );
};

export default ULThemeLink;
